import { FC } from "react";
import { createPortal } from "react-dom";

import { NO_DATA_STROKE, NO_RACE } from "./colors";
import type { Hatch, Paint } from "./types";

/* Shared pieces of the web maps (SVG + DOM, imported by *.web.tsx only). */

/* us-atlas@3 maps (2017 shapes), served from public/maps with Connecticut
   and Alaska swapped for the Census 2024 shapes: Connecticut's planning
   regions (2022) and Alaska's Chugach and Copper River (2019). Their state
   outlines come from the same shapes, so the borders line up. */
export const COUNTIES_URL = "/maps/us-counties-10m.json";
export const STATES_URL = "/maps/us-states-10m.json";
export const MAP_HEIGHT = 500;
export const MAP_WIDTH = 800;
export const SELECTED_STROKE = "rgb(var(--color-typography-900))";

/** us-atlas features carry their FIPS code as `id`. */
export interface GeoFeature extends GeoJSON.Feature {
  id: string;
  rsmKey: string;
}

export interface TooltipState {
  text: string;
  x: number;
  y: number;
}

export const uniqueHatches = (paints: Paint[]): Hatch[] => {
  const byId = new Map<string, Hatch>();
  for (const paint of paints) {
    if (paint.hatch) byId.set(paint.hatch.id, paint.hatch);
  }
  return [...byId.values()];
};

/** Geography style for a paint (default, hover and pressed states). */
export const geographyStyle = (
  paint: Paint | undefined,
  selected: boolean,
  clickable: boolean
) => {
  const base = {
    cursor: clickable ? "pointer" : "default",
    fill: paint
      ? paint.hatch
        ? `url(#${paint.hatch.id})`
        : paint.fill
      : NO_RACE,
    opacity: paint?.muted ? 0.45 : 1,
    outline: "none",
    stroke: selected
      ? SELECTED_STROKE
      : paint?.dashed
        ? NO_DATA_STROKE
        : "#fff",
    strokeDasharray: paint?.dashed && !selected ? "3 2" : undefined,
    strokeWidth: selected ? 1.5 : 0.75,
    transition: "fill 300ms ease",
  };
  return {
    default: base,
    hover: clickable
      ? {
          ...base,
          stroke: SELECTED_STROKE,
          strokeDasharray: undefined,
          strokeWidth: 1.25,
        }
      : base,
    pressed: base,
  };
};

/** SVG patterns: the base fill with 45° stripes, one per distinct hatch. */
export const HatchDefs: FC<{ hatches: Hatch[] }> = ({ hatches }) => (
  <defs>
    {hatches.map((hatch) => (
      <pattern
        height={7}
        id={hatch.id}
        key={hatch.id}
        patternTransform="rotate(45)"
        patternUnits="userSpaceOnUse"
        width={7}
      >
        <rect fill={hatch.base} height={7} width={7} />
        <line
          stroke={hatch.stripe}
          strokeWidth={2.5}
          x1={1}
          x2={1}
          y1={0}
          y2={7}
        />
      </pattern>
    ))}
  </defs>
);

export const MapTooltip: FC<{ tooltip: TooltipState | null }> = ({
  tooltip,
}) =>
  tooltip && tooltip.text
    ? createPortal(
        <div
          style={{
            background: "rgb(var(--color-background-800))",
            borderRadius: 6,
            color: "rgb(var(--color-typography-0))",
            fontSize: 12,
            left: tooltip.x + 12,
            lineHeight: "17px",
            maxWidth: 320,
            padding: "6px 10px",
            pointerEvents: "none",
            position: "fixed",
            top: tooltip.y + 12,
            whiteSpace: "pre-line",
            zIndex: 9999,
          }}
        >
          {tooltip.text}
        </div>,
        document.body
      )
    : null;
